import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { enGB } from 'date-fns/locale'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ApiError } from '@/api/client'
import App from '@/App'
import { createAppTheme } from '@/theme'
import { useAppearanceStore } from '@/themeStore'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry a rejection the server meant; only transient failures.
        if (error instanceof ApiError && error.status < 500) return false
        return failureCount < 2
      },
    },
    mutations: { retry: false },
  },
})

/** Rebuilds the theme only when the stored palette or mode actually changes. */
function ThemedApp() {
  const palette = useAppearanceStore((state) => state.palette)
  const mode = useAppearanceStore((state) => state.mode)
  const theme = useMemo(() => createAppTheme(palette, mode), [palette, mode])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* en-GB, not the browser's own locale: a date picker is one more place
          the office needs to read 21-11-2026 rather than whatever format the
          machine it is running on happens to default to. */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemedApp />
  </React.StrictMode>,
)
